"use client";
import React from "react";
import {
  AccreditationSmallSection,
  Footer,
  Hero,
} from "@/.storyblok/types/319751/storyblok-components";
import TrustSmall from "@/components/layouts/TrustSmall";
import QuoteFormHeader from "@/components/layouts/QuoteHeader";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import Social from "@/components/layouts/Social";
import { useBlogContext } from "@/contexts/BlogContext";
import { useJargonBusterContext } from '@/contexts/JargonBusterContext';
import Link from "next/link";
import Image from "next/image";
import { renderRichText } from "@/lib/richText";

interface HeroProps {
  blok?: Hero & SbBlokData;
  trustSmall?: AccreditationSmallSection & SbBlokData;
  footer?: Footer & SbBlokData;
}

const BlogHero: React.FC<HeroProps> = ({ blok, footer }) => {
  const {
    searchTerm,
    setSearchTerm,
    selectedTopic,
    setSelectedTopic,
    resetFilters,
    isSearching,
    totalResults,
    availableTopics = [],
    topicCounts = {},
  } = useBlogContext();

  const { title, subtitle } = blok || {};
  const { contact_section } = footer || {};

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle topic selection
  const handleTopicClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const topic = target.value;
    const wasChecked = target.dataset.waschecked === "true";

    // Update the was-checked state
    target.dataset.waschecked = wasChecked ? "false" : "true";

    if (wasChecked) {
      // If it was already checked, uncheck it
      target.checked = false;
      setSelectedTopic("");
    } else {
      // Otherwise, set it as checked
      target.checked = true;
      setSelectedTopic(topic);
    }
  };

  // Handle search reset
  const handleReset = () => {
    resetFilters();

    // Reset UI elements
    document.querySelectorAll("input.blog-topic").forEach((input: Element) => {
      const topicInput = input as HTMLInputElement;
      topicInput.checked = false;
      topicInput.dataset.waschecked = "false";
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setSearchTerm("");
      e.preventDefault();
    } else if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <div {...(blok && storyblokEditable(blok))} className="hero">
      <div className="hero-inner">
        <h1 className="mb-4" style={{ textRendering: 'optimizeSpeed' }}>{title}&nbsp;</h1>
        <p className="hero-subtitle">{subtitle}</p>

        <form
          className="row align-items-center justify-content-center mt-4"
          action=""
          method="get"
          onSubmit={(e) => e.preventDefault()}
        >
          {/* Search Field */}
          <div className="col col-22 col-lg-12 p-0">
            <label className="sr-only" htmlFor="blog-search">Search the blog</label>
            <div
              className="hero-search-field rounded-3 d-flex align-items-center p-0 position-relative mb-3 mb-lg-4 mb-xl-5">
              <i className="fas fa-search me-1"></i>
              <input
                className="border-0 bg-transparent flex-grow-1 p-2 px-4 rounded-3 hero-search-input text-white"
                type="search"
                id="blog-search-input"
                name="blog-search-input"
                placeholder="Search blog…"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
              />
              <span
                className="hero-search-results text-white text-left"
                id="blog-search-results"
                aria-live="polite"
              >
                {isSearching && totalResults !== undefined && (
                  totalResults === 1 ? "1 result" : `${totalResults} results`
                )}
              </span>
              <button
                className="hero-search-reset"
                type="button"
                id="blog-search-reset"
                data-bs-toggle="tooltip"
                title="Reset search"
                onClick={handleReset}
                style={{ display: isSearching ? "block" : "none" }}
              >
                <i className="fa fa-times m-0"></i>
              </button>
            </div>
          </div>

          <div className="row align-items-center text-white">
            <div className="col-auto flex-grow w-100">
              <h2 className="visually-hidden">Tags</h2>
              <div className="blog-topics d-flex flex-wrap w-100">
                {availableTopics.map((topic) => {
                  const topicSlug = topic.toLowerCase().replace(/\s/g, "-");
                  const count = topicCounts[topic] || 0;

                  return (
                    <React.Fragment key={topic}>
                      <input
                        type="radio"
                        className="blog-topic d-none"
                        name="blog-topic"
                        id={`topic-${topicSlug}`}
                        value={topicSlug}
                        data-waschecked={topicSlug === selectedTopic ? "true" : "false"}
                        checked={topicSlug === selectedTopic}
                        onClick={handleTopicClick}
                        readOnly
                      />
                      <label
                        htmlFor={`topic-${topicSlug}`}
                        className="bg-primary blog-topic d-inline-block text-nowrap small text-white rounded-3 me-1 fw-bolder"
                      >
                        {topic}
                        <span className="blog-topic-count ms-1">{count}</span>
                      </label>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Social Media Links */}
            <div className="blog-social col text-end text-nowrap">
              <h2 className="small d-inline-block text-white mb-0 me-2 align-middle fw-bolder">
                Social
              </h2>
              {Array.isArray(contact_section) && contact_section[0]?.socials?.length ? (
                <Social color="white" social_links={contact_section[0].socials} />
              ) : null}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Fallback for when blog context is not available but we need to show a blog hero
const BlogHeroFallback: React.FC<HeroProps> = ({ blok }) => {
  const { title, subtitle } = blok || {};

  return (
    <div {...(blok && storyblokEditable(blok))} className="hero">
      <div className="hero-inner">
        <h1 className="mb-4" style={{ textRendering: 'optimizeSpeed' }}>{title}&nbsp;</h1>
        <p className="hero-subtitle">{subtitle}</p>
        <div className="alert alert-warning">
          Blog functionality is not available on this page.
        </div>
      </div>
    </div>
  );
};

// Main Hero component that doesn't use the blog context
const HeroComponent: React.FC<HeroProps> = ({ blok, trustSmall, footer }) => {
  if (!blok) return null;

  const { title, subtitle, hero_type, form_title, cta_button, cta_button_link, image, date, trust_small_label, rich_text, use_different_trust_small, select_different_trust_small, button_label} = blok;

  // Determine which trust small content to use
  const effectiveTrustSmall = use_different_trust_small &&
  Array.isArray(select_different_trust_small) &&
  select_different_trust_small.length > 0
    ? select_different_trust_small[0]?.content
    : trustSmall;

  // Helper function to render TrustSmall component
  const renderTrustSmall = () => {
    if (!effectiveTrustSmall) return null;

    return (
      <TrustSmall
        blok={effectiveTrustSmall as AccreditationSmallSection & SbBlokData}
        title={trust_small_label}
      />
    );
  };


  const formatEventDate = (dateString: string) => {
    const eventDate = new Date(dateString);
    const day = eventDate.getDate();
    const month = eventDate.toLocaleString('en-US', { month: 'long' });

    return { day, month };
  };


  if (hero_type === 'webinars' || hero_type === 'events') {
    let formattedDate;
    if (typeof date === 'string') {
      try {
        formattedDate = formatEventDate(date);
      } catch {
        formattedDate = null;
      }
    }

    return (
      <div className={`hero ${hero_type === 'events' ? 'event' : 'event-webinar'}`}>
        <div className="hero-inner">
          <div className="row justify-content-center align-items-center gx-4 gy-3 gy-md-0">
            <div className="col-auto">
              {formattedDate && (
                <div className="event-calender-date">
                  <div className="event-type"></div>
                  <div className="event-date">
                    <span>{formattedDate.day}</span>
                    {formattedDate.month}
                  </div>
                </div>
              )}
            </div>
            <div className="col-md-16 col-lg-18 col-xxl-16 text-center text-md-start">
              <h1 style={{ textRendering: 'optimizeSpeed' }}>{title}&nbsp;</h1>
              <p className="text-white lead mb-0">{subtitle}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If it's a blog hero, render the blog-specific component inside a try-catch
  if (hero_type === "blog_home") {
    try {
      return <BlogHero blok={blok} footer={footer} />;
    } catch {
      return <BlogHeroFallback blok={blok} />;
    }
  }

  if (hero_type === "center_align") {
    return (
      <div {...storyblokEditable(blok)} className="hero">
        <div className="hero-inner">
          <h1 style={{ textRendering: 'optimizeSpeed' }}>{title}&nbsp;</h1>
          {subtitle && (
            <p className="hero-subtitle">{subtitle}</p>
          )}
          {cta_button && cta_button_link?.url && (
            <Link className="btn btn-primary btn-lg js-cta-btn" href={cta_button_link.url}>
              {cta_button}
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (hero_type === "center_text_only") {
    return (
      <div {...(blok && storyblokEditable(blok))} className="hero">
        <div className="hero-inner">
          <h1 className="my-4" style={{ textRendering: 'optimizeSpeed' }}>{title}&nbsp;</h1>
        </div>
      </div>
    );
  }

  if (hero_type === "hero_author") {
    return (
      <div className="hero">
        <div className="hero-inner">
          <h1 className="my-4" style={{ textRendering: 'optimizeSpeed' }}><span className="text-primary d-block">{title}</span>{subtitle}</h1>
        </div>
      </div>
    );
  }

  if (hero_type === "cyber_essentials") {
    return (
      <div {...storyblokEditable(blok)} className="hero">
        <div className="hero-inner">
          {image?.filename && (
            <Image
              height={image.height || 164}
              width={image.width || 134}
              alt={image.alt || "Cyber Essentials"}
              title={image.title || "Cyber Essentials"}
              className="d-none d-lg-inline mb-4 mt-n4 js-lazy-load"
              src={image.filename}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              loading="eager"
              quality={75}
            />
          )}
          <h1 style={{ textRendering: 'optimizeSpeed' }}>{title}&nbsp;</h1>
          {subtitle && (
            <p className="hero-subtitle mb-0">{subtitle}</p>
          )}
        </div>
      </div>
    );
  }

  const JargonBusterHero: React.FC<HeroProps> = ({ blok }) => {
    const {
      searchTerm,
      setSearchTerm,
      resetFilters,
      isSearching,
      totalResults,
    } = useJargonBusterContext();

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        setSearchTerm("");
        e.preventDefault();
      } else if (e.key === "Enter") {
        e.preventDefault();
      }
    };

    return (
      <div {...(blok ? storyblokEditable(blok) : {})} className="hero">
      <div className="hero-inner">
          <h1 style={{ textRendering: 'optimizeSpeed' }}>{blok?.title}</h1>
          {blok?.subtitle && (
            <p className="hero-subtitle">{blok.subtitle}</p>
          )}

          <form
            className="row align-items-center justify-content-center mt-4"
            action=""
            method="get"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="col col-22 col-lg-12 p-0">
              <label className="sr-only" htmlFor="jargon-search">Search the glossary</label>
              <div className="hero-search-field rounded-3 d-flex align-items-center p-0 position-relative mb-3 mb-lg-4 mb-xl-5">
                <i className="fas fa-search me-1"></i>
                <input
                  className="border-0 bg-transparent flex-grow-1 p-2 px-4 rounded-3 hero-search-input text-white"
                  type="search"
                  id="jargon-search-input"
                  name="jargon-search-input"
                  placeholder="Search glossary…"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                />
                <span
                  className="hero-search-results text-white text-left"
                  id="jargon-search-results"
                  aria-live="polite"
                >
                {isSearching && totalResults !== undefined && (
                  totalResults === 1 ? "1 result" : `${totalResults} results`
                )}
              </span>
                <button
                  className="hero-search-reset"
                  type="button"
                  id="jargon-search-reset"
                  data-bs-toggle="tooltip"
                  title="Reset search"
                  onClick={resetFilters}
                  style={{ display: isSearching ? "block" : "none" }}
                >
                  <i className="fa fa-times m-0"></i>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (hero_type === "jargon_buster") {
    try {
      return <JargonBusterHero blok={blok} />;
    } catch {
      return (
        <div {...storyblokEditable(blok)} className="hero">
          <div className="hero-inner">
            <h1 style={{ textRendering: 'optimizeSpeed' }}>{title}&nbsp;</h1>
            {subtitle && <p className="hero-subtitle">{subtitle}</p>}
          </div>
        </div>
      );
    }
  }

  if (hero_type === "with_image") {
    return (
      <div {...(blok && storyblokEditable(blok))} className="hero">
        <div className="hero-inner">
          <div className="row text-start mb-3 mb-lg-0 align-items-center">
            <div className="col-24 col-lg-13 mb-4 mb-lg-0">
              <h1 style={{ textRendering: 'optimizeSpeed' }}>{title}&nbsp;</h1>
              <p className="hero-subtitle mb-4">{subtitle}</p>
              {trustSmall && <TrustSmall blok={trustSmall} title={trust_small_label} />}
            </div>
            {image?.filename && (
              <div className="col-24 col-lg-10 offset-lg-1 mt-5 mt-lg-0">
                <Image
                  src={image.filename}
                  alt={image.alt || title || ""}
                  width={image.width || 500}
                  height={image.height || 500}
                  className="img-fluid"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="eager"
                  quality={75}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...(blok && storyblokEditable(blok))} className="hero">
      <div className="hero-inner">
        {hero_type === "with_quote" && (
          <div className="row text-start mb-3 mb-lg-0 align-items-center">
            <div className="col-24 col-lg-13 mb-4 mb-lg-0">
              <h1 style={{ textRendering: 'optimizeSpeed' }}>{title}&nbsp;</h1>
              {subtitle ? (
                <p className="hero-subtitle mb-4">{subtitle}</p>
              ): null}
              {rich_text && typeof renderRichText(rich_text) === 'string' ? (
                <div className="hero-subtitle mb-4" dangerouslySetInnerHTML={{ __html: renderRichText(rich_text) }} />
              ) : null}
              {renderTrustSmall()}
            </div>
            {form_title && (
              <div className="col-24 col-lg-10 offset-lg-1 mt-5 mt-lg-0">
                <QuoteFormHeader formTitle={form_title} buttonLabel={button_label || "Submit"} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroComponent;